import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiConfigs } from "../../constants/apiConfigs";
import { MediaProductType, MediaType, Post } from "../../types/influencer/influencerTypes";
import { isFresh } from "../../utils/helpers";
import { ERROR } from "../../constants/errors";
import { fetchPosts, fetchSocialPosts } from "../../services/api/influencer";
import { SocialPost } from "../../types/collaborations/CollaborationTypes";


interface InfluencerContent {
    items: Post[],
    last_fetched_at: number | null,
    page: number,
    count: number,
    has_more: boolean,
    loading: boolean,
    last_fetched_at_server: string
}

interface SocialPosts {
    items: SocialPost[],
    last_fetched_at: number | null,
    page: number,
    count: number,
    has_more: boolean,
    loading: boolean,
    last_fetched_at_server: string
}


export interface InfluencerContentSlice {
    loading: boolean,
    data: {
        [key: string]: InfluencerContent
    },
    social_posts: Record<string, {
        [key: string]: SocialPosts
    }>
}

export const initialState: InfluencerContentSlice = {
    loading: false,
    data: {},
    social_posts: {}
}

const STALE_TIME = apiConfigs.FETCH_INFLUENCER_CONTENT.staleTime;

export const fetchInfluencerContent = createAsyncThunk(
    'influencer/content',
    async (data: { mediaProductType: MediaType, page: number, force?: boolean }, { getState, rejectWithValue }) => {
        const state = getState() as { influencerContent: InfluencerContentSlice };
        const mediaType = data.mediaProductType;
        if (data.page === 1 && !data.force && isFresh(state.influencerContent.data?.[mediaType]?.last_fetched_at, STALE_TIME
        )) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const media = await fetchPosts(mediaType, data.page);
            return { media, ...data };
        } catch (error) {
            console.log(error)
            return rejectWithValue(ERROR.NOT_FOUND);
        }
    }
)

export const fetchSocialPostsThunk = createAsyncThunk(
    'influencer/social-posts',
    async (data: { socialAccountId: string, mediaProductType: MediaProductType, page: number, force?: boolean }, { getState, rejectWithValue }) => {
        const state = getState() as { influencerContent: InfluencerContentSlice };
        const mediaType = data.mediaProductType;
        const socialAccountId = data.socialAccountId;
        const staleTime = apiConfigs.FETCH_SOCIAL_POSTS.staleTime;
        if (data.page === 1 && !data.force && isFresh(state.influencerContent.social_posts?.[socialAccountId]?.[mediaType]?.last_fetched_at, staleTime
        )) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const media = await fetchSocialPosts(socialAccountId, mediaType, data.page);
            return { media, ...data };
        } catch (error) {
            console.log(error)
            return rejectWithValue(ERROR.NOT_FOUND);
        }
    }
)

const influencerContentSlice = createSlice({
    name: 'influencerContent',
    initialState,
    reducers: {
        setLoading: (state, data) => {
            state.loading = data.payload;
        },
        markExpired: (state) => {
            state.data = {};
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchInfluencerContent.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchInfluencerContent.fulfilled, (state, action) => {
                const existing_items = state.data?.[action.payload.mediaProductType]?.items ? [...state.data[action.payload.mediaProductType].items] : [];
                const items = action.payload.page === 1 ? action.payload.media.items : [...existing_items, ...action.payload.media.items]
                state.data = {
                    ...(state.data ?? {}),
                    [action.payload.mediaProductType]: {
                        items,
                        page: action.payload.page,
                        last_fetched_at: Date.now(),
                        has_more: items.length < action.payload.media.count,
                        count: action.payload.media.count,
                        loading: false,
                        last_fetched_at_server: action.payload.media.last_fetched_at
                    }
                }
                state.loading = false;
            })
            .addCase(fetchInfluencerContent.rejected, (state, action) => {
                state.loading = false;
            })
            .addCase(fetchSocialPostsThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSocialPostsThunk.fulfilled, (state, action) => {
                const socialAccountId = action.payload.socialAccountId;
                const mediaProductType = action.payload.mediaProductType;
                const existing_items = state.social_posts?.[socialAccountId]?.[mediaProductType]?.items ? [...state.social_posts[socialAccountId][mediaProductType].items] : [];
                const items = action.payload.page === 1 ? action.payload.media.items : [...existing_items, ...action.payload.media.items]
                state.social_posts = {
                    ...(state.social_posts ?? {}),
                    [socialAccountId]: {
                        ...(state.social_posts?.[socialAccountId] ?? {}),
                        [mediaProductType]: {
                            items,
                            page: action.payload.page,
                            last_fetched_at: Date.now(),
                            has_more: items.length < action.payload.media.count,
                            count: action.payload.media.count,
                            loading: false,
                            last_fetched_at_server: action.payload.media.last_fetched_at
                        }
                    }
                }
                state.loading = false;
            })
            .addCase(fetchSocialPostsThunk.rejected, (state) => {
                state.loading = false;
            })
    }
})

export const { setLoading, markExpired } = influencerContentSlice.actions;

export default influencerContentSlice.reducer